import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import axiosInstance from "../../axiosInstance";
 
import PublicLayout from "../components/PublicLayout";

/* ─── helpers ─── */
const getInitials = (value) =>
  String(value || "JB").split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const CATEGORY_ICONS = [
  "ri-code-s-slash-line", "ri-briefcase-line", "ri-settings-3-line",
  "ri-bar-chart-line", "ri-palette-line", "ri-hospital-line",
  "ri-book-open-line", "ri-truck-line",
];

const asArray = (value) => (Array.isArray(value) ? value : []);

const hasValue = (value) =>
  value !== undefined && value !== null && String(value).trim() !== "";

const firstValue = (...values) => values.find(hasValue);

const firstFilledObject = (items = []) =>
  asArray(items).find((item) =>
    item && typeof item === "object" && Object.values(item).some(hasValue)
  ) || {};

const pickImage = (...values) =>
  firstValue(
    ...values.filter((value) => !Array.isArray(value)),
    ...values.flatMap((value) => {
      if (Array.isArray(value)) {
        return value.flatMap((item) =>
          item && typeof item === "object"
            ? [item.imageInput, item.image, item.url, item.src]
            : [item]
        );
      }
      return [];
    })
  );

const pickLink = (...values) =>
  firstValue(
    ...values.filter((value) => !Array.isArray(value)),
    ...values.flatMap((value) => {
      if (Array.isArray(value)) {
        return value.flatMap((item) =>
          item && typeof item === "object"
            ? [item.imageUrlInput, item.link, item.url, item.href]
            : [item]
        );
      }
      return [];
    })
  );

const normalizePath = (value) => {
  if (!hasValue(value)) return "/jobs";
  const link = String(value).trim();
  if (link.startsWith("http://") || link.startsWith("https://") || link.startsWith("/")) {
    return link;
  }
  return `/${link}`;
};

const splitLines = (value, fallback = []) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        return item?.text || item?.title || item?.label || "";
      })
      .filter(hasValue);
  }

  if (!hasValue(value)) return fallback;

  return String(value)
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const parseFaqItems = (value, fallback = null) => {
  if (typeof value === "string" && hasValue(value)) {
    const parsed = value
      .split(/\r?\n/)
      .map((line) => {
        const [question, ...answerParts] = line.split("|");
        return {
          q: question?.trim() || "",
          a: answerParts.join("|").trim(),
        };
      })
      .filter((item) => hasValue(item.q) && hasValue(item.a));
    if (parsed.length) return parsed;
  }

  if (Array.isArray(value)) {
    const parsed = value
      .map((item) => ({
        q: item?.q || item?.question || item?.title || "",
        a: item?.a || item?.answer || item?.desc || item?.description || "",
      }))
      .filter((item) => hasValue(item.q) && hasValue(item.a));
    if (parsed.length) return parsed;
  }
  return fallback;
};

const parsePipeLines = (value, mapper, fallback = null) => {
  if (!hasValue(value)) return fallback;

  const parsed = String(value)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => mapper(line.split("|").map((part) => part.trim()), index))
    .filter(Boolean);

  return parsed.length ? parsed : fallback;
};

const parseStatsItems = (value, fallback) =>
  parsePipeLines(
    value,
    ([icon, statValue, label]) =>
      hasValue(statValue) && hasValue(label)
        ? { icon: icon || "ri-bar-chart-box-line", value: statValue, label }
        : null,
    fallback
  );

const parseHowSteps = (value, fallback) =>
  parsePipeLines(
    value,
    ([icon, title, desc, badge, badgeText], index) =>
      hasValue(title) && hasValue(desc)
        ? {
            step: index + 1,
            icon: icon || "ri-checkbox-circle-line",
            title,
            desc,
            badge: badge || "ri-star-line",
            badgeText: badgeText || "",
          }
        : null,
    fallback
  );

const parseTestimonials = (value, fallback) =>
  parsePipeLines(
    value,
    ([name, role, company, text, rating]) =>
      hasValue(name) && hasValue(text)
        ? {
            name,
            role: role || "Candidate",
            company: company || "gojobify",
            text,
            rating: Number(rating) || 5,
          }
        : null,
    fallback
  );

const parseCategoryItems = (value, fallback = []) =>
  parsePipeLines(
    value,
    ([icon, name, description, badge, link]) =>
      hasValue(name)
        ? {
            icon: icon || "ri-briefcase-line",
            name,
            description: description || "",
            badge: badge || "",
            link: link || "",
          }
        : null,
    fallback
  );

/* ─── Stat counter ─── */
const StatItem = ({ value, label }) => (
  <div className="q-stat-item">
    <strong>{value}</strong>
    <span>{label}</span>
  </div>
);

/* ─── Main component ─── */
const PublicHome = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [homeData, setHomeData] = useState(null);
  const [homeLayout, setHomeLayout] = useState(null);
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [sendingAppLink, setSendingAppLink] = useState(false);
  const [testimonials] = useState([
    { name: "Arjun Sharma", role: "Software Engineer", company: "Tech Corp", text: "Found my dream job within 2 weeks. The platform made the whole process seamless and stress-free.", rating: 5 },
    { name: "Priya Mehta", role: "Product Manager", company: "StartupXYZ", text: "Amazing platform for both employers and job seekers. The talent pool here is exceptional.", rating: 5 },
    { name: "Rahul Gupta", role: "UX Designer", company: "DesignLab", text: "The job matching is incredibly accurate. Got interviews at top companies within days.", rating: 5 },
  ]);

  useEffect(() => {
    const load = async () => {
      try {
        const [homeRes, layoutRes, jobsRes, catRes, blogRes] = await Promise.all([
          axiosInstance.get("/home-data"),
          axiosInstance.get("/home-layout-data"),
          axiosInstance.get("/job-portal/jobs/public?limit=6&sort=newest"),
          axiosInstance.get("/get-all-specialization"),
          axiosInstance.get("/all-blogs-latest"),
        ]);
        const jobs = jobsRes.data?.data?.jobs || [];
        setHomeData(homeRes.data?.homeData || null);
        setHomeLayout(layoutRes.data?.homeLayout || null);
        setFeaturedJobs(jobs);
        setActiveJob(jobs[0] || null);
        setCategories((catRes.data?.Specialization || []).slice(0, 8));
        setLatestBlogs(blogRes.data?.blogs || []);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const stats = useMemo(() => [
    { label: "Active Jobs", value: featuredJobs.length ? `${featuredJobs.length * 500}+` : "45k+" },
    { label: "New Platform Providers", value: "15min+" },
    { label: "Freelancers Available", value: "2000+" },
  ], [featuredJobs.length]);

  const sendAppLink = async (e) => {
    e.preventDefault();
    if (!searchTerm) {
      toast.error("Please enter your email ID");
      return;
    }

    try {
      setSendingAppLink(true);
      await axiosInstance.post("/send-app-link/", { email: searchTerm });
      toast.success("App link sent to your email");
      setSearchTerm("");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to send app link right now");
    } finally {
      setSendingAppLink(false);
    }
  };

  const bannerOne = firstFilledObject(homeLayout?.trending_product_banner);
  const bannerTwo = firstFilledObject(homeLayout?.latest_product_banner);
  const appProfileBanner = firstFilledObject(homeLayout?.latest_product_carousal);

  const googlePlayUrl = normalizePath(
    pickLink(
      homeLayout?.google_play_url,
      homeData?.android_app_url,
      homeLayout?.play_store_url,
      homeLayout?.collection_url,
      bannerOne.imageUrlInput
    ) || "/jobs"
  );
  const appStoreUrl = normalizePath(
    pickLink(
      homeLayout?.app_store_url,
      homeData?.ios_app_url,
      homeLayout?.ios_store_url,
      bannerTwo.imageUrlInput,
      googlePlayUrl
    ) || "/jobs"
  );
  const appScreenImage = pickImage(
    homeLayout?.app_screen_image,
    homeLayout?.mobile_app_image,
    homeLayout?.slider_img,
    homeLayout?.collection_img,
    homeLayout?.home_slider,
    homeLayout?.trending_product_carousal
  );

  const heroFeatures = splitLines(homeLayout?.hero_features, [
    "Get notifications for new jobs and application updates",
    "View job details and company information in one place",
  ]);

  const downloadFeatures = splitLines(homeLayout?.download_app_features || bannerTwo.imageParaInput, [
    "Easy Apply",
    "Real-time Alerts",
    "Application Tracker",
  ]);

  const profileFeatures = splitLines(homeLayout?.profile_section_features || appProfileBanner.imageTITInput, [
    "Browse and search jobs by role, skills, or location",
    "Apply easily using your saved profile or resume",
    "Get notifications for new jobs and application updates",
    "View job details and company information in one place",
  ]);

  const faqItems =
    parseFaqItems(homeLayout?.faq_items) ||
    parseFaqItems(homeLayout?.faqs) ||
    parseFaqItems(homeLayout?.faq_items_text) ||
    FAQ_ITEMS;

  const heroKicker = firstValue(homeLayout?.hero_kicker, homeLayout?.top_bar, "Stay Ahead in Your Career");
  const heroTitle = firstValue(homeLayout?.hero_title, bannerOne.imageTITInput, "Get Job Alerts.");
  const heroHighlightPrefix = firstValue(homeLayout?.hero_highlight_prefix, "Find");
  const heroHighlight = firstValue(homeLayout?.hero_highlight, "Better Opportunities.");
  const heroDescription = firstValue(
    homeLayout?.hero_description,
    homeLayout?.collection_paragraph,
    homeData?.meta_description,
    "Explore thousands of jobs and apply with ease."
  );
  const featuredJobsTitle = firstValue(homeLayout?.featured_jobs_heading, "Featured Job Circulars");
  const featuredJobsSubtitle = firstValue(
    homeLayout?.featured_jobs_subheading,
    "Fresh openings from active employers."
  );
  const downloadTitle = firstValue(homeLayout?.download_app_heading, bannerTwo.imageTITInput, "Get gojobify App");
  const downloadSubtitle = firstValue(homeLayout?.download_app_title, "Apply anytime, anywhere!");
  const downloadDescription = firstValue(
    homeLayout?.download_app_description,
    bannerTwo.imageParaInput,
    "Find jobs, track applications and get hired on the go."
  );
  const qrImage = pickImage(homeLayout?.qr_image, homeLayout?.download_qr_image, homeLayout?.service_logos);
  const profileTitle = firstValue(homeLayout?.profile_section_heading, homeLayout?.collection_heading, "Stand Out Among Recruiters");
  const profileHighlight = firstValue(homeLayout?.profile_section_highlight, "with a Profile");
  const profileDescription = firstValue(
    homeLayout?.profile_section_description,
    homeLayout?.collection_paragraph,
    "Available for both Android and iOS. Get personalized job recommendations, apply instantly, and track applications from your pocket."
  );
  const profileImage = pickImage(
    homeLayout?.profile_section_image,
    homeLayout?.collection_img,
    appProfileBanner.imageInput,
    "/assets/img/jobsimage.webp"
  );
  const featuredJobsButtonText = firstValue(homeLayout?.featured_jobs_button_text, "View All Jobs");
  const featuredJobsButtonLink = normalizePath(firstValue(homeLayout?.featured_jobs_button_link, "/jobs"));
  const featuredJobsEmptyText = firstValue(homeLayout?.featured_jobs_empty_text, "No featured jobs available.");
  const statStripItems = parseStatsItems(homeLayout?.stats_items_text, [
    { icon: "ri-briefcase-4-line", value: "45,000+", label: "Jobs Available" },
    { icon: "ri-building-4-line", value: "5,000+", label: "Companies" },
    { icon: "ri-team-line", value: "1,500+", label: "Recruiters" },
    { icon: "ri-award-line", value: "98%", label: "Success Rate" },
  ]);
  const trustedLabel = firstValue(homeLayout?.trusted_label, "Trusted by 30k+ Businesses");
  const trustedBrands = splitLines(homeLayout?.trusted_brands_text, [
    "Coinbase",
    "LightNodes",
    "Slack",
    "Dropbox",
    "Atlassian",
    "Oracle",
    "Semrush",
  ]);
  const defaultLogoMarqueeItems = [
    
  ];
  const uploadedLogoMarqueeItems = asArray(homeLayout?.service_logos)
    .map((item, index) => ({
      image: firstValue(item?.imageInput, item?.image, item?.src),
      link: firstValue(item?.imageUrlInput, item?.link, item?.href),
      alt: item?.title || item?.name || `Hiring company logo ${index + 1}`,
    }))
    .filter((item) => hasValue(item.image));
  const logoMarqueeItems = uploadedLogoMarqueeItems.length
    ? uploadedLogoMarqueeItems
    : defaultLogoMarqueeItems;
  const categoryKicker = firstValue(homeLayout?.category_kicker, "Browse Categories");
  const categoryHeading = firstValue(homeLayout?.category_heading, "Popular Job Categories");
  const categoryDescription = firstValue(
    homeLayout?.category_description,
    "Explore high-demand roles across the most active areas."
  );
  const categoryButtonText = firstValue(homeLayout?.category_button_text, "See All Categories");
  const categoryButtonLink = normalizePath(firstValue(homeLayout?.category_button_link, "/jobs"));
  const categoryCardDescription = firstValue(
    homeLayout?.category_card_description,
    "Browse roles tailored to this category."
  );
  const categoryCardBadge = firstValue(homeLayout?.category_card_badge, "Active Hiring");
  const categoryItems = parseCategoryItems(
    homeLayout?.category_items_text,
    categories.map((cat, i) => ({
      icon: CATEGORY_ICONS[i % CATEGORY_ICONS.length],
      name: cat.name,
      description: categoryCardDescription,
      badge: categoryCardBadge,
      link: `/jobs?department=${encodeURIComponent(cat.name)}`,
    }))
  );
  const categoryEmptyText = firstValue(homeLayout?.category_empty_text, "No categories available.");
  const howKicker = firstValue(homeLayout?.how_kicker, "How It Works");
  const howHeading = firstValue(homeLayout?.how_heading, "Find Your Dream Job in 3 Steps");
  const howDescription = firstValue(homeLayout?.how_description, "Simple, fast, and designed to get you hired.");
  const howSteps = parseHowSteps(homeLayout?.how_steps_text, [
    { step: 1, icon: "ri-user-add-line", title: "Create Your Profile", desc: "Sign up in minutes. Add your skills, experience, and career goals. Our AI instantly tailors job matches to your profile.", badge: "ri-time-line", badgeText: "Takes only 2 minutes" },
    { step: 2, icon: "ri-search-eye-line", title: "Explore & Apply", desc: "Browse thousands of curated listings. Use smart filters by location, salary, and category. Apply in one click using your saved profile.", badge: "ri-briefcase-line", badgeText: "45,000+ active jobs" },
  ]);
  const testimonialKicker = firstValue(homeLayout?.testimonial_kicker, "Testimonials");
  const testimonialHeading = firstValue(homeLayout?.testimonial_heading, "Reviewed by the Community,");
  const testimonialHighlight = firstValue(homeLayout?.testimonial_highlight, "Trusted by Professionals");
  const testimonialItems = parseTestimonials(homeLayout?.testimonial_items_text, testimonials);
  const qrLabel = firstValue(homeLayout?.qr_label, "Scan QR Code\nto Download App");
  const profileEmailPlaceholder = firstValue(homeLayout?.profile_email_placeholder, "Enter Email ID");
  const profileButtonText = firstValue(homeLayout?.profile_button_text, "Get link");
  const heroEmailPlaceholder = firstValue(homeLayout?.hero_email_placeholder, profileEmailPlaceholder);
  const heroButtonText = firstValue(homeLayout?.hero_button_text, "Send App Link");
  const heroPrivacyNote = firstValue(homeLayout?.hero_privacy_note, "We respect your privacy. No spam, ever.");
  const googlePlayLabel = firstValue(homeLayout?.google_play_label, "GET IT ON");
  const googlePlayTitle = firstValue(homeLayout?.google_play_title, "Google Play");
  const appStoreLabel = firstValue(homeLayout?.app_store_label, "Download on the");
  const appStoreTitle = firstValue(homeLayout?.app_store_title, "App Store");
  const featuredJobsApplyText = firstValue(homeLayout?.featured_jobs_apply_text, "Apply Now");
  const featuredJobsPostedText = firstValue(homeLayout?.featured_jobs_posted_text, "Posted 1d ago");
  const blogKicker = firstValue(homeLayout?.blog_kicker, "Blog");
  const blogHeading = firstValue(homeLayout?.blog_heading, "Insights & Updates");
  const blogButtonText = firstValue(homeLayout?.blog_button_text, "Read More");
  const blogButtonLink = normalizePath(firstValue(homeLayout?.blog_button_link, "/career-advice"));
  const blogCategoryLabel = firstValue(homeLayout?.blog_category_label, "Career Advice");
  const blogDescription = firstValue(homeLayout?.blog_description, "Practical guidance from our hiring and career experts.");
  const blogReadText = firstValue(homeLayout?.blog_read_text, "Continue Reading");
  const faqKicker = firstValue(homeLayout?.faq_kicker, "FAQ");
  const faqButtonText = firstValue(homeLayout?.faq_button_text, "Ask a Question");
  const faqButtonLink = normalizePath(firstValue(homeLayout?.faq_button_link, "/contact-us"));
  const heroAlertTitle = firstValue(homeLayout?.hero_alert_title, "New Job Alert");
  const heroAlertSubtitle = firstValue(homeLayout?.hero_alert_subtitle, "Software Engineer at Amazon");
  const heroAlertTime = firstValue(homeLayout?.hero_alert_time, "1m ago");

  return (
    <>
      <style>{`
        /* ── Reset & Base ── */
        body { background: #f8f7ff; }
        .q-home-redesign {
          background: linear-gradient(180deg, #f5f2ff 0%, #ffffff 54%, #f7f4ff 100%);
          padding: 54px 0 34px;
          overflow: hidden;
        }
        .q-trusted-band,
        .q-stats-section {
          display: none;
        }
        .q-alert-hero {
          display: grid;
          grid-template-columns: minmax(0, .98fr) minmax(340px, 1.02fr);
          gap: 38px;
          align-items: center;
        }
        .q-alert-copy h1 {
          font-size: clamp(2.4rem, 5vw, 4.4rem);
          line-height: 1.08;
          font-weight: 950;
          color: #081029;
          margin: 0 0 22px;
        }
        .q-alert-copy h1 span { color: #6d28d9; }
        .q-alert-kicker {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: #eee8ff;
          color: #5b21b6;
          border-radius: 999px;
          padding: 12px 18px;
          font-weight: 800;
          margin-bottom: 26px;
        }
        .q-alert-copy p {
          color: #667085;
          font-size: 1.12rem;
          line-height: 1.7;
          margin-bottom: 26px;
        }
        .q-alert-feature {
          display: grid;
          grid-template-columns: 58px 1fr;
          gap: 16px;
          align-items: center;
          margin-bottom: 18px;
          color: #11182f;
          font-weight: 700;
        }
        .q-alert-feature-icon {
          width: 54px;
          height: 54px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          background: #eee8ff;
          color: #6d28d9;
          font-size: 25px;
        }
        .q-app-link-box {
          display: grid;
          grid-template-columns: 92px 1fr auto;
          align-items: center;
          max-width: 570px;
          margin: 30px 0 18px;
          background: #fff;
          border: 1px solid #ebe5ff;
          border-radius: 14px;
          padding: 8px;
          box-shadow: 0 18px 44px rgba(77, 51, 150, .12);
        }
        .q-country-code {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          color: #11182f;
          font-weight: 800;
          min-height: 58px;
          border-right: 1px solid #edf0f5;
        }
        .q-app-link-box input {
          border: 0;
          outline: 0;
          min-height: 58px;
          padding: 0 18px;
          color: #11182f;
          font-size: 1rem;
        }
        .q-app-link-box button,
        .q-gradient-btn {
          border: 0;
          min-height: 58px;
          border-radius: 10px;
          padding: 0 24px;
          background: linear-gradient(135deg, #6d28d9, #8b5cf6);
          color: #fff;
          font-weight: 900;
          box-shadow: 0 16px 28px rgba(109, 40, 217, .22);
        }
        .q-privacy-note {
          display: flex;
          gap: 8px;
          align-items: center;
          color: #667085;
          font-size: .9rem;
          margin-bottom: 24px;
        }
        .q-store-row {
          display: flex;
          flex-wrap: wrap;
          gap: 18px;
        }
        .q-store-pill {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          min-width: 220px;
          min-height: 70px;
          padding: 12px 20px;
          border-radius: 12px;
          background: #070b16;
          color: #fff;
          text-decoration: none;
        }
        .q-store-pill:hover { color: #fff; }
        .q-store-pill i { font-size: 34px; }
        .q-store-pill small { display: block; font-size: .72rem; line-height: 1; opacity: .85; }
        .q-store-pill strong { display: block; font-size: 1.4rem; line-height: 1.1; }
        .q-phone-scene {
          position: relative;
          min-height: 620px;
        }
        .q-phone-screen-img {
          width: min(440px, 100%);
          max-height: 620px;
          display: block;
          margin: 0 auto;
          object-fit: contain;
          filter: drop-shadow(0 30px 60px rgba(28, 21, 80, .24));
          position: relative;
          z-index: 2;
        }
        .q-phone-card {
          position: absolute;
          left: 50%;
          top: 0;
          width: min(360px, 68vw);
          min-height: 560px;
          transform: translateX(-50%) rotate(-5deg);
          border: 12px solid #151a2c;
          border-radius: 42px;
          background: #fff;
          box-shadow: 0 30px 70px rgba(28, 21, 80, .28);
          padding: 34px 24px 24px;
          z-index: 2;
        }
        .q-phone-logo {
          color: #11182f;
          font-weight: 950;
          text-align: center;
          margin-bottom: 30px;
        }
        .q-phone-logo span { color: #f97316; }
        .q-phone-title {
          text-align: center;
          color: #11182f;
          font-weight: 900;
          font-size: 1.55rem;
          line-height: 1.35;
          margin-bottom: 22px;
        }
        .q-phone-search {
          display: grid;
          grid-template-columns: 1fr 46px;
          gap: 10px;
          align-items: center;
          min-height: 58px;
          border-radius: 14px;
          background: #f8f7ff;
          padding: 8px;
          margin-bottom: 24px;
        }
        .q-phone-search span {
          color: #98a2b3;
          padding-left: 12px;
          font-size: .86rem;
        }
        .q-phone-search button {
          width: 44px;
          height: 44px;
          border: 0;
          border-radius: 50%;
          color: #fff;
          background: #6d28d9;
        }
        .q-phone-job {
          border-radius: 16px;
          border: 1px solid #eee9ff;
          padding: 18px;
          box-shadow: 0 12px 28px rgba(77, 51, 150, .08);
        }
        .q-phone-nav {
          position: absolute;
          left: 24px;
          right: 24px;
          bottom: 18px;
          display: flex;
          justify-content: space-between;
          color: #667085;
          font-size: .75rem;
        }
        .q-floating-alert,
        .q-floating-check,
        .q-floating-person {
          position: absolute;
          z-index: 3;
          box-shadow: 0 18px 44px rgba(77, 51, 150, .14);
        }
        .q-floating-alert {
          right: 0;
          top: 132px;
          width: 235px;
          border-radius: 16px;
          background: #fff;
          padding: 18px;
          color: #11182f;
        }
        .q-floating-check {
          right: 28px;
          top: 318px;
          width: 72px;
          height: 72px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          color: #fff;
          background: #6d28d9;
          font-size: 34px;
        }
        .q-floating-person {
          right: 26px;
          bottom: 8px;
          width: 178px;
          height: 208px;
          border-radius: 32px 32px 14px 14px;
          display: grid;
          place-items: center;
          color: #fff;
          background: linear-gradient(180deg, #9f7aea 0%, #5b21b6 100%);
          font-size: 92px;
        }
        .q-recommended-panel,
        .q-stat-strip,
        .q-download-banner {
          background: #fff;
          border: 1px solid #eee9ff;
          border-radius: 16px;
          box-shadow: 0 16px 42px rgba(77, 51, 150, .09);
        }
        .q-recommended-panel {
          margin-top: 30px;
          padding: 26px;
        }
        .q-logo-marquee {
          margin-top: 30px;
          padding: 22px 0 8px;
        }
        .q-logo-marquee-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 18px;
        }
        .q-logo-marquee-title {
          color: #11182f;
          font-size: .92rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0;
        }
        .q-logo-marquee-note {
          color: #667085;
          font-size: .9rem;
          margin: 0;
        }
        .q-logo-swiper {
          overflow: hidden;
          padding: 4px 0 16px;
          position: relative;
        }
        .q-logo-swiper::before,
        .q-logo-swiper::after {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          width: 78px;
          z-index: 2;
          pointer-events: none;
        }
        .q-logo-swiper::before {
          left: 0;
          background: linear-gradient(90deg, #f7f3ff 0%, rgba(247,243,255,0) 100%);
        }
        .q-logo-swiper::after {
          right: 0;
          background: linear-gradient(270deg, #f7f3ff 0%, rgba(247,243,255,0) 100%);
        }
        .q-logo-card {
          min-height: 86px;
          border: 1px solid #eee9ff;
          border-radius: 14px;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          box-shadow: 0 10px 28px rgba(77, 51, 150, .07);
        }
        .q-logo-card img {
          display: block;
          width: 100%;
          max-width: 150px;
          height: 48px;
          object-fit: contain;
        }
        .q-recommended-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          margin-bottom: 22px;
        }
        .q-recommended-title {
          display: flex;
          align-items: center;
          gap: 14px;
          color: #11182f;
          font-size: 1.32rem;
          font-weight: 900;
        }
        .q-recommended-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 20px;
        }
        .q-rec-card {
          border: 1px solid #edf0f5;
          border-radius: 14px;
          padding: 22px;
          color: #11182f;
          text-decoration: none;
          transition: .2s ease;
        }
        .q-rec-card:hover {
          transform: translateY(-3px);
          border-color: #c4b5fd;
          color: #11182f;
        }
        .q-rec-top {
          display: grid;
          grid-template-columns: 58px 1fr;
          gap: 16px;
          align-items: center;
          margin-bottom: 18px;
        }
        .q-rec-logo {
          width: 58px;
          height: 58px;
          border-radius: 12px;
          display: grid;
          place-items: center;
          background: #f1edff;
          color: #5b21b6;
          font-weight: 900;
          border: 1px solid #ded6ff;
        }
        .q-rec-title { font-weight: 900; line-height: 1.3; }
        .q-rec-company {
          color: #667085;
          font-size: .92rem;
          margin-top: 4px;
        }
        .q-rec-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 14px;
          color: #667085;
          font-size: .9rem;
          margin-bottom: 16px;
        }
        .q-rec-pills {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 18px;
        }
        .q-rec-pill {
          border-radius: 999px;
          background: #eee8ff;
          color: #6d28d9;
          padding: 6px 12px;
          font-weight: 800;
          font-size: .82rem;
        }
        .q-rec-footer {
          display: flex;
          justify-content: space-between;
          gap: 16px;
          align-items: center;
          border-top: 1px solid #edf0f5;
          padding-top: 16px;
          color: #667085;
          font-size: .9rem;
        }
        .q-stat-strip {
          margin-top: 18px;
          padding: 24px 28px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }
        .q-stat-pill {
          display: grid;
          grid-template-columns: 62px 1fr;
          gap: 16px;
          align-items: center;
        }
        .q-stat-pill-icon {
          width: 58px;
          height: 58px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          background: #eee8ff;
          color: #6d28d9;
          font-size: 28px;
        }
        .q-stat-pill strong {
          display: block;
          color: #11182f;
          font-size: 1.35rem;
          font-weight: 950;
        }
        .q-stat-pill span {
          color: #667085;
          font-size: .92rem;
        }
        .q-download-banner {
          margin-top: 24px;
          min-height: 210px;
          padding: 34px 44px;
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 36px;
          align-items: center;
          overflow: hidden;
          background: linear-gradient(135deg, #6d28d9, #8b5cf6);
          color: #fff;
        }
        .q-download-banner h2 {
          font-size: clamp(1.6rem, 3vw, 2.35rem);
          font-weight: 950;
          margin: 0 0 10px;
          color: #fff;
        }
        .q-download-checks {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          margin-top: 22px;
          font-weight: 800;
        }
        .q-download-banner .q-qr-box {
          width: 142px;
          height: 142px;
          display: grid;
          place-items: center;
          border-radius: 12px;
          background: #fff;
          color: #11182f;
          font-size: 80px;
        }
        .q-download-banner .q-qr-box img {
          width: 112px;
          height: 112px;
          object-fit: contain;
        }
 
        /* ── Hero ── */
        .q-hero {
          background: linear-gradient(135deg, #f0eeff 0%, #e8f4ff 50%, #f5f0ff 100%);
          padding: 80px 0 60px;
          position: relative;
          overflow: hidden;
        }
        .q-hero::before {
          content: '';
          position: absolute;
          width: 600px; height: 600px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(108,92,231,0.08) 0%, transparent 70%);
          top: -200px; right: -100px;
          pointer-events: none;
        }
        .q-hero-grid {
          display: grid;
          grid-template-columns: 1fr 480px;
          gap: 48px;
          align-items: center;
        }
        .q-hero-kicker {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: rgba(108,92,231,0.1);
          color: #6c5ce7;
          font-size: 0.8rem;
          font-weight: 600;
          padding: 5px 12px;
          border-radius: 100px;
          margin-bottom: 20px;
          letter-spacing: 0.3px;
        }
        .q-hero-kicker::before {
          content: '';
          width: 6px; height: 6px;
          background: #6c5ce7;
          border-radius: 50%;
        }
        .q-hero h1 {
          font-size: clamp(2rem, 4vw, 3.2rem);
          font-weight: 900;
          line-height: 1.1;
          color: #0f0e17;
          margin-bottom: 18px;
          letter-spacing: -1px;
        }
        .q-hero h1 .highlight {
          color: #6c5ce7;
          position: relative;
        }
        .q-hero h1 .highlight-yellow {
          color: #f7b731;
        }
        .q-hero-desc {
          color: #666;
          font-size: 1rem;
          line-height: 1.7;
          margin-bottom: 32px;
          max-width: 460px;
        }
        .q-search-box {
          background: #fff;
          border-radius: 14px;
          padding: 8px 8px 8px 20px;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 0 4px 24px rgba(108,92,231,0.12);
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .q-search-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 0.9rem;
          color: #333;
          background: transparent;
          min-width: 120px;
        }
        .q-search-divider {
          width: 1px; height: 28px;
          background: #eee;
          flex-shrink: 0;
        }
        .q-search-select {
          border: none;
          outline: none;
          font-size: 0.85rem;
          color: #555;
          background: transparent;
          padding: 4px 8px;
          cursor: pointer;
        }
        .q-search-btn {
          background: #6c5ce7;
          color: #fff;
          border: none;
          border-radius: 10px;
          padding: 12px 24px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .q-search-btn:hover {
          background: #5a4bd1;
          transform: translateY(-1px);
        }
        .q-popular-tags {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .q-popular-label {
          font-size: 0.8rem;
          color: #888;
          font-weight: 500;
        }
        .q-tag {
          display: inline-block;
          background: rgba(108,92,231,0.08);
          color: #6c5ce7;
          font-size: 0.78rem;
          font-weight: 500;
          padding: 4px 12px;
          border-radius: 100px;
          text-decoration: none;
          transition: all 0.2s;
        }
        .q-tag:hover {
          background: #6c5ce7;
          color: #fff;
        }

        /* Hero Right - Job Card Panel */
        .q-hero-panel {
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 8px 40px rgba(108,92,231,0.12);
          overflow: hidden;
        }
        .q-hero-panel-header {
          padding: 18px 20px;
          border-bottom: 1px solid #f0eeff;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .q-hero-panel-header strong {
          font-size: 0.9rem;
          color: #333;
        }
        .q-hero-panel-body {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 420px;
          overflow-y: auto;
        }
        .q-job-mini-card {
          padding: 14px;
          border-radius: 12px;
          border: 1.5px solid #f0f0f0;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
          display: block;
          color: inherit;
        }
        .q-job-mini-card:hover,
        .q-job-mini-card.active {
          border-color: #6c5ce7;
          background: #f8f6ff;
        }
        .q-job-mini-top {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 8px;
        }
        .q-company-badge {
          width: 38px; height: 38px;
          border-radius: 10px;
          background: linear-gradient(135deg, #6c5ce7, #a29bfe);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 0.75rem;
          font-weight: 700;
          flex-shrink: 0;
        }
        .q-job-mini-info h4 {
          font-size: 0.875rem;
          font-weight: 700;
          color: #222;
          margin: 0 0 2px;
          line-height: 1.3;
        }
        .q-job-mini-info p {
          font-size: 0.78rem;
          color: #888;
          margin: 0;
        }
        .q-pill {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 100px;
          font-size: 0.72rem;
          font-weight: 600;
          background: #eef6f0;
          color: #27ae60;
        }
        .q-pill-purple {
          background: #f0eeff;
          color: #6c5ce7;
        }
        .q-job-mini-meta {
          display: flex;
          gap: 10px;
          font-size: 0.75rem;
          color: #aaa;
          flex-wrap: wrap;
        }

        /* Trusted logos */
        .q-trusted-band {
          background: #fff;
          padding: 20px 0;
          border-bottom: 1px solid #eee;
        }
        .q-trusted-inner {
          display: flex;
          align-items: center;
          gap: 32px;
          overflow-x: auto;
          scrollbar-width: none;
        }
        .q-trusted-inner::-webkit-scrollbar { display: none; }
        .q-trusted-label {
          font-size: 0.82rem;
          color: #999;
          font-weight: 600;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .q-trusted-logo {
          font-size: 0.85rem;
          font-weight: 700;
          color: #bbb;
          white-space: nowrap;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .q-trusted-logo i { font-size: 1.1rem; }

        /* Stats */
        .q-stats-section {
          background: #fff;
          padding: 40px 0;
          border-bottom: 1px solid #eee;
        }
        .q-stats-grid {
          display: flex;
          justify-content: center;
          gap: 80px;
          flex-wrap: wrap;
        }
        .q-stat-item {
          text-align: center;
        }
        .q-stat-item strong {
          display: block;
          font-size: 2.2rem;
          font-weight: 900;
          color: #0f0e17;
          letter-spacing: -1px;
        }
        .q-stat-item span {
          font-size: 0.85rem;
          color: #888;
          font-weight: 500;
        }

        /* Categories */
        .q-section { padding: 72px 0; }
        .q-section-subtle { padding: 72px 0; background: #f8f7ff; }
        .q-section-dark { padding: 72px 0; background: #0f0e17; }
        .q-kicker {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
          font-weight: 700;
          color: #6c5ce7;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 8px;
        }
        .q-section-title {
          font-size: clamp(1.5rem, 3vw, 2.2rem);
          font-weight: 900;
          color: #0f0e17;
          letter-spacing: -0.5px;
          margin: 0 0 8px;
        }
        .q-section-title-light {
          color: #fff;
        }
        .q-section-sub {
          color: #888;
          font-size: 0.95rem;
          margin: 0;
        }
        .q-section-sub-light { color: #aaa; }
        .q-section-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-bottom: 36px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .q-btn-outline {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 9px 20px;
          border: 1.5px solid #ddd;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          color: #333;
          text-decoration: none;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .q-btn-outline:hover {
          border-color: #6c5ce7;
          color: #6c5ce7;
        }
        .q-btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 9px 20px;
          background: #6c5ce7;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          color: #fff;
          text-decoration: none;
          transition: all 0.2s;
          border: 1.5px solid #6c5ce7;
          white-space: nowrap;
        }
        .q-btn-primary:hover {
          background: #5a4bd1;
          color: #fff;
          transform: translateY(-1px);
        }

        /* Category grid */
        .q-cat-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        .q-cat-card {
          background: #fff;
          border: 1.5px solid #eee;
          border-radius: 16px;
          padding: 24px 20px;
          text-decoration: none;
          transition: all 0.25s;
          display: block;
        }
        .q-cat-card:hover {
          border-color: #6c5ce7;
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(108,92,231,0.12);
        }
        .q-cat-icon-wrap {
          width: 48px; height: 48px;
          border-radius: 12px;
          background: #f0eeff;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
        }
        .q-cat-icon-wrap i { font-size: 1.4rem; color: #6c5ce7; }
        .q-cat-name {
          font-size: 0.95rem;
          font-weight: 700;
          color: #222;
          margin-bottom: 6px;
        }
        .q-cat-sub {
          font-size: 0.78rem;
          color: #aaa;
          margin-bottom: 12px;
        }
        .q-cat-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6c5ce7;
          background: #f0eeff;
          padding: 3px 10px;
          border-radius: 100px;
        }

        /* Featured Jobs */
        .q-jobs-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        /* Payments Section */
        .q-payment-section {
          background: linear-gradient(135deg, #f0eeff 0%, #e8f5ff 100%);
          padding: 72px 0;
        }
        .q-payment-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }
        .q-payment-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #6c5ce7;
          color: #fff;
          padding: 6px 14px;
          border-radius: 100px;
          font-size: 0.8rem;
          font-weight: 600;
          margin-bottom: 20px;
        }
        .q-payment-title {
          font-size: clamp(1.6rem, 3vw, 2.4rem);
          font-weight: 900;
          color: #0f0e17;
          margin-bottom: 16px;
          letter-spacing: -0.5px;
          line-height: 1.2;
        }
        .q-payment-title span { color: #6c5ce7; }
        .q-payment-features {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-top: 24px;
        }
        .q-payment-feature {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 2px 12px rgba(108,92,231,0.08);
        }
        .q-feature-icon {
          width: 40px; height: 40px;
          border-radius: 10px;
          background: #f0eeff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .q-feature-icon i { font-size: 1.1rem; color: #6c5ce7; }
        .q-feature-title {
          font-size: 0.9rem;
          font-weight: 700;
          color: #222;
          margin-bottom: 3px;
        }
        .q-feature-desc { font-size: 0.82rem; color: #888; }

        /* Onboarding section */
        .q-onboard-section { padding: 72px 0; background: #fff; }
        .q-onboard-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }
        .q-onboard-visual {
          background: linear-gradient(135deg, #0f0e17 0%, #1e1d2e 100%);
          border-radius: 20px;
          padding: 40px;
          color: #fff;
          position: relative;
          overflow: hidden;
        }
        .q-onboard-visual::before {
          content: '';
          position: absolute;
          width: 300px; height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(108,92,231,0.3) 0%, transparent 70%);
          top: -100px; right: -100px;
        }
        .q-world-stat {
          font-size: 3rem;
          font-weight: 900;
          color: #a29bfe;
          line-height: 1;
          margin-bottom: 4px;
        }
        .q-onboard-list {
          list-style: none;
          padding: 0;
          margin: 24px 0;
        }
        .q-onboard-list li {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.875rem;
          color: #ccc;
          margin-bottom: 12px;
        }
        .q-onboard-list li::before {
          content: '';
          width: 6px; height: 6px;
          border-radius: 50%;
          background: #6c5ce7;
          flex-shrink: 0;
        }

        /* Testimonials */
        .q-reviews-section { padding: 72px 0; background: #0f0e17; }
        .q-reviews-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        .q-review-card {
          background: #1e1d2e;
          border: 1px solid #2a2940;
          border-radius: 16px;
          padding: 24px;
          transition: all 0.2s;
        }
        .q-review-card:hover {
          border-color: #6c5ce7;
          transform: translateY(-2px);
        }
        .q-stars { color: #f7b731; font-size: 0.9rem; margin-bottom: 14px; }
        .q-review-text { color: #bbb; font-size: 0.875rem; line-height: 1.7; margin-bottom: 20px; }
        .q-reviewer {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .q-reviewer-avatar {
          width: 40px; height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6c5ce7, #a29bfe);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 0.85rem;
          font-weight: 700;
          flex-shrink: 0;
        }
        .q-reviewer-name { font-weight: 700; font-size: 0.875rem; color: #fff; margin-bottom: 2px; }
        .q-reviewer-role { font-size: 0.78rem; color: #666; }

        /* Hire by profession */
        .q-profession-section { padding: 72px 0; background: #f8f7ff; }
        .q-profession-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 32px;
          flex-wrap: wrap;
        }
        .q-prof-tab {
          padding: 8px 18px;
          border-radius: 100px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          border: 1.5px solid #ddd;
          background: #fff;
          color: #555;
          transition: all 0.2s;
        }
        .q-prof-tab.active, .q-prof-tab:hover {
          background: #6c5ce7;
          border-color: #6c5ce7;
          color: #fff;
        }
        .q-freelancer-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        .q-freelancer-card {
          background: #fff;
          border: 1.5px solid #eee;
          border-radius: 16px;
          padding: 24px;
          transition: all 0.25s;
          text-align: center;
        }
        .q-freelancer-card:hover {
          border-color: #6c5ce7;
          box-shadow: 0 8px 24px rgba(108,92,231,0.1);
          transform: translateY(-3px);
        }
        .q-fl-avatar {
          width: 64px; height: 64px;
          border-radius: 50%;
          background: linear-gradient(135deg, #6c5ce7, #a29bfe);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 1.2rem;
          font-weight: 700;
          margin: 0 auto 12px;
        }
        .q-fl-name { font-weight: 700; color: #222; margin-bottom: 4px; }
        .q-fl-role { font-size: 0.82rem; color: #888; margin-bottom: 10px; }
        .q-fl-skills {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .q-fl-skill {
          font-size: 0.72rem;
          background: #f0eeff;
          color: #6c5ce7;
          padding: 2px 8px;
          border-radius: 100px;
          font-weight: 500;
        }
        .q-fl-btn {
          display: inline-flex;
          padding: 7px 20px;
          background: #f0eeff;
          color: #6c5ce7;
          border-radius: 8px;
          font-size: 0.82rem;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.2s;
        }
        .q-fl-btn:hover {
          background: #6c5ce7;
          color: #fff;
        }

        /* FAQ */
        .q-faq-section { padding: 72px 0; background: #fff; }
        .q-faq-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: start;
        }
        .q-faq-item {
          border-bottom: 1px solid #f0f0f0;
          padding: 18px 0;
          cursor: pointer;
        }
        .q-faq-question {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-weight: 600;
          color: #222;
          font-size: 0.95rem;
          gap: 12px;
        }
        .q-faq-icon {
          width: 24px; height: 24px;
          border-radius: 50%;
          background: #f0eeff;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6c5ce7;
          flex-shrink: 0;
          font-size: 0.8rem;
          transition: all 0.2s;
        }
        .q-faq-answer {
          color: #888;
          font-size: 0.875rem;
          line-height: 1.7;
          margin-top: 10px;
          display: none;
        }
        .q-faq-item.open .q-faq-answer { display: block; }
        .q-faq-item.open .q-faq-icon { background: #6c5ce7; color: #fff; transform: rotate(45deg); }

        /* Blog */
        .q-blog-card {
          background: #fff;
          border: 1.5px solid #eee;
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.25s;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .q-blog-card:hover {
          border-color: #6c5ce7;
          box-shadow: 0 8px 24px rgba(108,92,231,0.1);
          transform: translateY(-3px);
        }
        .q-blog-img-placeholder {
          height: 180px;
          background: linear-gradient(135deg, #f0eeff 0%, #e8f5ff 100%);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .q-blog-img-placeholder i { font-size: 2.5rem; color: #c5bdf5; }
        .q-blog-body { padding: 20px; flex: 1; display: flex; flex-direction: column; }
        .q-blog-cat { font-size: 0.75rem; font-weight: 700; color: #6c5ce7; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
        .q-blog-title { font-size: 1rem; font-weight: 700; color: #222; margin-bottom: 10px; line-height: 1.4; }
        .q-blog-desc { font-size: 0.82rem; color: #888; margin-bottom: 16px; flex: 1; }
        .q-blog-read {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.82rem;
          font-weight: 700;
          color: #6c5ce7;
          text-decoration: none;
        }
        .q-blog-read:hover { gap: 10px; }

        /* Loader */
        .q-loader-shell {
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .q-spinner {
          width: 48px; height: 48px;
          border: 4px solid #f0eeff;
          border-top-color: #6c5ce7;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ── HOW IT WORKS ── */
.q-how-section { padding: 72px 0; background: #fff; }
.q-how-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  margin-top: 40px;
}
.q-how-card {
  background: #fff;
  border: 1.5px solid #eee;
  border-radius: 18px;
  padding: 32px 24px;
  position: relative;
  transition: all 0.25s;
}
.q-how-card:hover {
  border-color: #6c5ce7;
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(108,92,231,0.1);
}
.q-how-step-num {
  position: absolute;
  top: -16px; left: 24px;
  width: 32px; height: 32px;
  background: #6c5ce7;
  color: #fff;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.8rem; font-weight: 800;
}
.q-how-icon-wrap {
  width: 56px; height: 56px;
  background: #f0eeff;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 18px;
}
.q-how-icon-wrap i { font-size: 1.6rem; color: #6c5ce7; }
.q-how-title { font-size: 1.05rem; font-weight: 800; color: #222; margin-bottom: 10px; }
.q-how-desc { font-size: 0.875rem; color: #888; line-height: 1.7; margin: 0; }
.q-how-card-wrap { position: relative; }
.q-connector {
  display: flex; align-items: center; justify-content: center;
  position: absolute; right: -24px; top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  width: 32px; height: 32px;
  background: #fff;
  border: 1.5px solid #e0d9ff;
  border-radius: 50%;
  color: #6c5ce7;
  font-size: 0.75rem;
}

/* ── APP DOWNLOAD ── */
.q-app-section {
  background: linear-gradient(135deg, #f0eeff 0%, #e8f5ff 100%);
  padding: 72px 0;
  overflow: hidden;
}
.q-app-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}
.q-app-kicker {
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(108,92,231,0.12);
  color: #6c5ce7;
  font-size: 0.8rem; font-weight: 700;
  padding: 5px 14px; border-radius: 100px;
  margin-bottom: 18px;
  letter-spacing: 0.3px;
}
.q-app-title {
  font-size: clamp(1.7rem, 3vw, 2.4rem);
  font-weight: 900; color: #0f0e17;
  line-height: 1.15; letter-spacing: -0.5px;
  margin-bottom: 14px;
}
.q-app-title span { color: #6c5ce7; }
.q-app-desc { font-size: 0.95rem; color: #666; line-height: 1.7; margin-bottom: 28px; }
.q-app-features { display: flex; flex-direction: column; gap: 12px; margin-bottom: 32px; }
.q-app-feat-row { display: flex; align-items: center; gap: 10px; font-size: 0.875rem; color: #444; }
.q-feat-check {
  width: 22px; height: 22px; border-radius: 50%;
  background: #6c5ce7;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; font-size: 0.65rem; color: #fff;
}
.q-sms-box {
  display: flex; gap: 10px;
  background: #fff;
  border-radius: 12px;
  padding: 8px 8px 8px 16px;
  box-shadow: 0 4px 20px rgba(108,92,231,0.1);
  margin-bottom: 20px;
  max-width: 400px;
}
.q-sms-input {
  flex: 1; border: none; outline: none;
  font-size: 0.9rem; color: #333;
  background: transparent;
}
.q-sms-btn {
  background: #6c5ce7; color: #fff;
  border: none; border-radius: 8px;
  padding: 10px 20px; font-size: 0.875rem;
  font-weight: 700; cursor: pointer; white-space: nowrap;
  transition: all 0.2s;
}
.q-sms-btn:hover { background: #5a4bd1; }
.q-store-btns { display: flex; gap: 12px; flex-wrap: wrap; }
.q-store-btn {
  display: flex; align-items: center; gap: 10px;
  background: #0f0e17; color: #fff;
  border-radius: 10px;
  padding: 10px 18px;
  text-decoration: none;
  transition: all 0.2s;
  min-width: 140px;
}
.q-store-btn:hover { background: #1e1d2e; transform: translateY(-2px); color: #fff; }
.q-store-btn i { font-size: 1.4rem; }
.q-store-btn-text { line-height: 1.2; }
.q-store-btn-sub { font-size: 0.65rem; color: #aaa; }
.q-store-btn-name { font-size: 0.9rem; font-weight: 700; }
.q-app-visual { display: flex; flex-direction: column; gap: 16px; align-items: flex-end; }
.q-app-phone-mock {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 16px 48px rgba(108,92,231,0.15);
  padding: 20px;
  width: 100%; max-width: 360px;
}
.q-phone-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.q-phone-header strong { font-size: 0.9rem; color: #222; }
.q-phone-header a { font-size: 0.78rem; color: #6c5ce7; text-decoration: none; font-weight: 600; }
.q-phone-sub { font-size: 0.72rem; color: #aaa; margin-top: 2px; }
.q-mini-job-row {
  display: flex; gap: 10px; margin-bottom: 10px;
  padding: 12px;
  border: 1.5px solid #f0f0f0;
  border-radius: 12px;
  align-items: flex-start;
  transition: all 0.2s;
}
.q-mini-job-row:hover { border-color: #6c5ce7; background: #faf8ff; }
.q-mj-logo {
  width: 36px; height: 36px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.65rem; font-weight: 800; flex-shrink: 0;
}
.q-mj-logo.lava { background: #fff3e0; color: #e65100; }
.q-mj-logo.acc  { background: #e8f4fd; color: #0277bd; }
.q-mj-logo.ap   { background: #fce4ec; color: #c62828; }
.q-mj-info h5 { font-size: 0.8rem; font-weight: 700; color: #222; margin: 0 0 2px; }
.q-mj-info p  { font-size: 0.72rem; color: #888; margin: 0 0 4px; }
.q-mj-meta { display: flex; gap: 8px; font-size: 0.68rem; color: #aaa; }
.q-mj-top { display: flex; width: 100%; justify-content: space-between; align-items: flex-start; }
.q-mj-badge { font-size: 0.65rem; background: #f0eeff; color: #6c5ce7; padding: 2px 7px; border-radius: 100px; font-weight: 600; flex-shrink: 0; }
.q-rating-row { display: flex; align-items: center; gap: 4px; font-size: 0.68rem; color: #f7b731; }
.q-rating-row span { color: #888; font-size: 0.65rem; }
.q-qr-box {
  background: #fff;
  border-radius: 16px;
  padding: 16px 20px;
  display: flex; align-items: center; gap: 16px;
  box-shadow: 0 4px 16px rgba(108,92,231,0.1);
  max-width: 360px; width: 100%;
}
.q-qr-img {
  width: 70px; height: 70px;
  background: #0f0e17;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.q-qr-text h4 { font-size: 0.875rem; font-weight: 800; color: #222; margin: 0 0 4px; }
.q-qr-text p  { font-size: 0.78rem; color: #888; margin: 0; line-height: 1.4; }

/* ── RESPONSIVE additions ── */
@media (max-width: 991px) {
  .q-how-grid  { grid-template-columns: 1fr; }
  .q-app-grid  { grid-template-columns: 1fr; }
  .q-app-visual { align-items: stretch; }
  .q-app-phone-mock, .q-qr-box { max-width: 100%; }
  .q-connector { display: none; }
}

        /* Responsive */
        @media (max-width: 991px) {
          .q-alert-hero { grid-template-columns: 1fr; }
          .q-phone-scene { min-height: 520px; }
          .q-floating-alert { right: 12px; }
          .q-floating-person { display: none; }
          .q-recommended-grid { grid-template-columns: 1fr; }
          .q-stat-strip { grid-template-columns: repeat(2, 1fr); }
          .q-download-banner { grid-template-columns: 1fr; text-align: left; }
          .q-hero-grid { grid-template-columns: 1fr; }
          .q-hero-panel { display: none; }
          .q-cat-grid { grid-template-columns: repeat(2, 1fr); }
          .q-jobs-grid { grid-template-columns: repeat(2, 1fr); }
          .q-reviews-grid { grid-template-columns: 1fr; }
          .q-freelancer-grid { grid-template-columns: repeat(2, 1fr); }
          .q-payment-grid, .q-onboard-grid, .q-faq-grid { grid-template-columns: 1fr; }
          .q-stats-grid { gap: 40px; }
        }
        @media (max-width: 575px) {
          .q-home-redesign { padding: 34px 0 26px; }
          .q-app-link-box { grid-template-columns: 1fr; }
          .q-country-code { border-right: 0; border-bottom: 1px solid #edf0f5; justify-content: flex-start; padding-left: 12px; }
          .q-app-link-box button { width: 100%; }
          .q-phone-card { position: relative; width: 100%; min-height: 520px; transform: none; left: auto; }
          .q-phone-scene { min-height: auto; }
          .q-floating-alert, .q-floating-check { display: none; }
          .q-recommended-panel { padding: 18px; }
          .q-recommended-head { align-items: flex-start; flex-direction: column; }
          .q-logo-marquee-head { align-items: flex-start; flex-direction: column; }
          .q-logo-swiper::before,
          .q-logo-swiper::after { width: 34px; }
          .q-stat-strip { grid-template-columns: 1fr; padding: 20px; }
          .q-download-banner { padding: 28px 22px; }
          .q-store-pill { width: 100%; min-width: 0; }
          .q-cat-grid { grid-template-columns: 1fr; }
          .q-jobs-grid { grid-template-columns: 1fr; }
          .q-freelancer-grid { grid-template-columns: 1fr; }
          .q-stats-grid { gap: 24px; }
          .q-hero { padding: 48px 0 40px; }
        }
      `}</style>

 
 
    <PublicLayout> 
      <div className="q-home">
        {isLoading ? (
          <div className="q-loader-shell">
            <div>
              <div className="q-spinner mx-auto mb-3"></div>
              <p className="text-muted text-center">Loading...</p>
            </div>
          </div>
        ) : (
          <>


            {/* ── HERO ── */}
            <section className="q-home-redesign">
              <div className="container">
                <div className="q-alert-hero">
                  <div className="q-alert-copy">
                    <div className="q-alert-kicker">
                      <i className="ri-notification-3-line"></i> {heroKicker}
                    </div>
                    <h1>
                      {heroTitle}<br />
                      {heroHighlightPrefix} <span>{heroHighlight}</span>
                    </h1>
                    <p>
                      {heroDescription}
                    </p>
                    {heroFeatures.slice(0, 3).map((feature, index) => (
                      <div className="q-alert-feature" key={feature}>
                        <div className="q-alert-feature-icon">
                          <i className={index === 0 ? "ri-notification-4-line" : "ri-briefcase-4-line"}></i>
                        </div>
                        <span>{feature}</span>
                      </div>
                    ))}
                    <form onSubmit={sendAppLink} className="q-app-link-box">
                      <div className="q-country-code"><i className="ri-mail-line"></i> Email</div>
                      <input
                        type="email"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={heroEmailPlaceholder}
                      />
                      <button type="submit">
                        {sendingAppLink ? "Sending..." : heroButtonText} <i className="ri-send-plane-fill"></i>
                      </button>
                    </form>
                    <div className="q-privacy-note">
                      <i className="ri-lock-line"></i> {heroPrivacyNote}
                    </div>
                    <div className="q-store-row">
                      <a href={googlePlayUrl} className="q-store-pill" target={googlePlayUrl.startsWith("http") ? "_blank" : undefined} rel={googlePlayUrl.startsWith("http") ? "noopener noreferrer" : undefined}>
                        <i className="ri-google-play-fill"></i>
                        <span><small>{googlePlayLabel}</small><strong>{googlePlayTitle}</strong></span>
                      </a>
                      <a href={appStoreUrl} className="q-store-pill" target={appStoreUrl.startsWith("http") ? "_blank" : undefined} rel={appStoreUrl.startsWith("http") ? "noopener noreferrer" : undefined}>
                        <i className="ri-apple-fill"></i>
                        <span><small>{appStoreLabel}</small><strong>{appStoreTitle}</strong></span>
                      </a>
                    </div>
                  </div>

                  <div className="q-phone-scene">
                    {appScreenImage ? (
                      <img src={appScreenImage} alt="App screen" className="q-phone-screen-img" />
                    ) : (
                    <div className="q-phone-card">
                      <div className="q-phone-logo">Go<span>Jobify</span></div>
                      <div className="q-phone-title">Your Dream Job<br />is Just a Click Away!</div>
                      <div className="q-phone-search">
                        <span>Search jobs, roles or companies</span>
                        <button type="button" aria-label="Search"><i className="ri-search-line"></i></button>
                      </div>
                      <div className="q-phone-job">
                        <div className="q-rec-top">
                          <div className="q-rec-logo"><i className="ri-palette-line"></i></div>
                          <div>
                            <div className="q-rec-title">{activeJob?.title || "UI/UX Designer"}</div>
                            <div className="q-rec-company">{activeJob?.company?.name || "TechNova Solutions"}</div>
                          </div>
                        </div>
                        <div className="q-rec-meta">
                          <span><i className="ri-map-pin-line"></i> {activeJob?.location?.display || "New Delhi"}</span>
                        </div>
                        <div className="q-rec-pills">
                          <span className="q-rec-pill">Full Time</span>
                          <span className="q-rec-pill">{activeJob?.workMode || "Onsite"}</span>
                        </div>
                      </div>
                      <div className="q-phone-nav">
                        <span><i className="ri-home-5-fill"></i><br />Home</span>
                        <span><i className="ri-search-line"></i><br />Search</span>
                        <span><i className="ri-briefcase-line"></i><br />Jobs</span>
                        <span><i className="ri-notification-line"></i><br />Alerts</span>
                        <span><i className="ri-user-line"></i><br />Profile</span>
                      </div>
                    </div>
                    )}
                    <div className="q-floating-alert">
                      <div className="d-flex gap-3 align-items-start">
                        <div className="q-alert-feature-icon" style={{ width: 42, height: 42, fontSize: 20 }}>
                          <i className="ri-notification-3-line"></i>
                        </div>
                        <div>
                          <strong>{heroAlertTitle}</strong>
                          <div className="small text-muted">{heroAlertSubtitle}</div>
                          <div className="small text-muted">{heroAlertTime}</div>
                        </div>
                      </div>
                    </div>
                    <div className="q-floating-check"><i className="ri-check-line"></i></div>
                    <div className="q-floating-person"><i className="ri-user-smile-line"></i></div>
                  </div>
                </div>
              </div>
            </section>

            {/* ── TRUSTED BAND ── */}
            <section className="q-home-redesign" style={{ paddingTop: 0 }}>
              <div className="container">
                {logoMarqueeItems.length > 0 && (
                <div className="q-logo-marquee">
                  <Swiper
                    className="q-logo-swiper"
                    modules={[Autoplay]}
                    loop={logoMarqueeItems.length > 4}
                    speed={3500}
                    autoplay={{
                      delay: 0,
                      disableOnInteraction: false,
                      pauseOnMouseEnter: true,
                    }}
                    allowTouchMove
                    spaceBetween={16}
                    slidesPerView={2}
                    breakpoints={{
                      576: { slidesPerView: 3 },
                      768: { slidesPerView: 4 },
                      1200: { slidesPerView: 5 },
                    }}
                  >
                    {[...logoMarqueeItems, ...logoMarqueeItems].map((logo, index) => {
                      const logoLink = hasValue(logo.link) ? String(logo.link).trim() : "";
                      const isExternalLogoLink = logoLink.startsWith("http");

                      return (
                      <SwiperSlide key={`${logo.image}-${index}`}>
                        {logoLink ? (
                          <a className="q-logo-card" href={logoLink} target={isExternalLogoLink ? "_blank" : undefined} rel={isExternalLogoLink ? "noopener noreferrer" : undefined}>
                            <img src={logo.image} alt={logo.alt} />
                          </a>
                        ) : (
                          <div className="q-logo-card">
                            <img src={logo.image} alt={logo.alt} />
                          </div>
                        )}
                      </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div>
                )}

                <div className="q-recommended-panel">
                  <div className="q-recommended-head">
                    <div>
                      <div className="q-recommended-title">
                        <span className="q-alert-feature-icon"><i className="ri-briefcase-4-line"></i></span>
                        {featuredJobsTitle}
                      </div>
                      <p className="q-section-sub mt-2 mb-0">{featuredJobsSubtitle}</p>
                    </div>
                    <Link to={featuredJobsButtonLink} className="q-btn-outline">
                      {featuredJobsButtonText} <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                  <div className="q-recommended-grid">
                    {featuredJobs.length ? featuredJobs.map((job) => (
                      <Link key={job.id} to={`/jobs/${job.id}`} className="q-rec-card">
                        <div className="q-rec-top">
                          <div className="q-rec-logo">{getInitials(job.company?.name || job.title)}</div>
                          <div>
                            <div className="q-rec-title">{job.title}</div>
                            <div className="q-rec-company">{job.company?.name || "Featured Employer"}</div>
                          </div>
                        </div>
                        <div className="q-rec-meta">
                          <span><i className="ri-star-fill text-warning"></i> 4.8</span>
                          <span><i className="ri-map-pin-line"></i> {job.location?.display || "India"}</span>
                          <span><i className="ri-calendar-line"></i> 3-5 years</span>
                        </div>
                        <div className="q-rec-pills">
                          <span className="q-rec-pill">{(job.employmentType || "Full time").replaceAll("_", " ")}</span>
                          <span className="q-rec-pill">{job.workMode || "Onsite"}</span>
                        </div>
                        <div className="q-rec-footer">
                          <span>{featuredJobsPostedText}</span>
                          <span className="q-gradient-btn" style={{ minHeight: 44, display: "inline-flex", alignItems: "center" }}>
                            {featuredJobsApplyText} <i className="ri-arrow-right-line"></i>
                          </span>
                        </div>
                      </Link>
                    )) : (
                      <div className="text-center text-muted py-5">{featuredJobsEmptyText}</div>
                    )}
                  </div>
                </div>

                <div className="q-stat-strip">
                  {statStripItems.map((item) => (
                    <div className="q-stat-pill" key={item.label}>
                      <div className="q-stat-pill-icon"><i className={item.icon}></i></div>
                      <div><strong>{item.value}</strong><span>{item.label}</span></div>
                    </div>
                  ))}
                </div>

                <div className="q-download-banner">
                  <div>
                    <div className="h4 fw-bold text-white mb-2">{downloadTitle}</div>
                    <h2>{downloadSubtitle}</h2>
                    <p className="mb-0">{downloadDescription}</p>
                    <div className="q-download-checks">
                      {downloadFeatures.map((feature) => (
                        <span key={feature}><i className="ri-checkbox-circle-fill text-success"></i> {feature}</span>
                      ))}
                    </div>
                  </div>
                  <div className="q-qr-box">
                    {qrImage ? <img src={qrImage} alt="Download QR code" /> : <i className="ri-qr-code-line"></i>}
                  </div>
                  <div className="fw-bold">
                    {String(qrLabel).split(/\r?\n/).map((line, index, lines) => (
                      <span key={`${line}-${index}`}>{line}{index < lines.length - 1 && <br />}</span>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <div className="q-trusted-band">
              <div className="container">
                <div className="q-trusted-inner">
                  <span className="q-trusted-label">{trustedLabel}</span>
                  {trustedBrands.map((b) => (
                    <span key={b} className="q-trusted-logo">
                      <i className="ri-verified-badge-fill" style={{ color: "#6c5ce7" }}></i> {b}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* ── STATS ── */}
            <section className="q-stats-section">
              <div className="container">
                <div className="q-stats-grid">
                  {stats.map((s) => <StatItem key={s.label} {...s} />)}
                </div>
              </div>
            </section>

            {/* ── CATEGORIES ── */}
            <section className="q-section-subtle">
              <div className="container">
                <div className="q-section-head">
                  <div>
                    <div className="q-kicker">{categoryKicker}</div>
                    <h2 className="q-section-title">{categoryHeading}</h2>
                    <p className="q-section-sub">{categoryDescription}</p>
                  </div>
                  <Link to={categoryButtonLink} className="q-btn-outline">
                    {categoryButtonText} <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="q-cat-grid">
                  {categoryItems.length > 0 ? categoryItems.map((cat, i) => (
                    <Link key={`${cat.name}-${i}`} to={normalizePath(cat.link || `/jobs?department=${encodeURIComponent(cat.name)}`)} className="q-cat-card">
                      <div className="q-cat-icon-wrap">
                        <i className={cat.icon || CATEGORY_ICONS[i % CATEGORY_ICONS.length]}></i>
                      </div>
                      <div className="q-cat-name">{cat.name}</div>
                      <div className="q-cat-sub">{cat.description || categoryCardDescription}</div>
                      <span className="q-cat-badge"><i className="ri-briefcase-line"></i> {cat.badge || categoryCardBadge}</span>
                    </Link>
                  )) : (
                    <div className="text-center text-muted py-5">{categoryEmptyText}</div>
                  )}
                </div>
              </div>
            </section>

             <section className="q-how-section">
    <div className="container">
      <div className="text-center">
        <div className="q-kicker">{howKicker}</div>
        <h2 className="q-section-title">{howHeading}</h2>
        <p className="q-section-sub">{howDescription}</p>
      </div>
      <div className="q-how-grid mt-5">
        {howSteps.map(({ step, icon, title, desc, badge, badgeText }) => (
          <div key={step} className="q-how-card-wrap">
            <div className="q-how-card">
              <div className="q-how-step-num">{step}</div>
              <div className="q-how-icon-wrap"><i className={icon}></i></div>
              <div className="q-how-title">{title}</div>
              <p className="q-how-desc">{desc}</p>
              {badgeText && (
                <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", color: "#6c5ce7", fontWeight: 600 }}>
                  <i className={badge}></i> {badgeText}
                </div>
              )}
            </div>
            {step < howSteps.length && <div className="q-connector"><i className="ri-arrow-right-line"></i></div>}
          </div>
        ))}
      </div>
    </div>
  </section>



            {/* ── FEATURED JOBS ── */}
            
            {/* <section className="q-section">
              <div className="container">
                <div className="q-section-head">
                  <div>
                    <div className="q-kicker">Featured Jobs</div>
                    <h2 className="q-section-title">Featured Job Circulars</h2>
                  </div>
                  <Link to="/jobs" className="q-btn-outline">
                    View All Jobs <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="q-jobs-grid">
                  {featuredJobs.map((job) => (
                    <PublicJobCard job={job} key={job.id} />
                  ))}
                  {featuredJobs.length === 0 && (
                    <div className="col-span-3 text-center text-muted py-5">No featured jobs available.</div>
                  )}
                </div>
              </div>
            </section> */}

            {/* ── PAYMENTS SIMPLIFIED ── */}
            {/* <section className="q-payment-section">
              <div className="container">
                <div className="q-payment-grid">
                  <div>
                    <div className="q-payment-badge">
                      <i className="ri-shield-check-line"></i> Trusted Platform
                    </div>
                    <h2 className="q-payment-title">
                      Payments <span>Simplified</span><br />with {homeData?.meta_title || "Quiety"}
                    </h2>
                    <p style={{ color: "#666", fontSize: "0.95rem", lineHeight: 1.7 }}>
                      Our secure payment infrastructure ensures freelancers get paid on time and clients get exactly what they ordered.
                    </p>
                    <div className="q-payment-features">
                      {[
                        { icon: "ri-file-list-3-line", title: "Calm Invoicing Chase", desc: "Automated invoice generation and sending so you never miss a payment." },
                        { icon: "ri-exchange-dollar-line", title: "Multi-Currency Payments", desc: "Accept payments from anywhere in the world in local currencies." },
                        { icon: "ri-shield-line", title: "No More Over-Budget Surprises", desc: "Transparent pricing and real-time budget tracking for every project." },
                      ].map((f) => (
                        <div key={f.title} className="q-payment-feature">
                          <div className="q-feature-icon"><i className={f.icon}></i></div>
                          <div>
                            <div className="q-feature-title">{f.title}</div>
                            <div className="q-feature-desc">{f.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ background: "#fff", borderRadius: 20, padding: 32, boxShadow: "0 8px 40px rgba(108,92,231,0.1)" }}>
                      <div style={{ background: "linear-gradient(135deg, #0f0e17, #1e1d2e)", borderRadius: 16, padding: 28, color: "#fff", marginBottom: 20 }}>
                        <div style={{ fontSize: "0.8rem", color: "#888", marginBottom: 12 }}>Platform Stats</div>
                        <div style={{ fontSize: "2.5rem", fontWeight: 900, color: "#a29bfe" }}>2.4M+</div>
                        <div style={{ fontSize: "0.85rem", color: "#aaa" }}>Payments processed this month</div>
                      </div>
                      {[
                        { label: "Escrow Protection", val: "100%" },
                        { label: "On-time Payment Rate", val: "98.5%" },
                        { label: "Dispute Resolution", val: "< 24h" },
                      ].map((item) => (
                        <div key={item.label} style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #f0f0f0" }}>
                          <span style={{ fontSize: "0.875rem", color: "#555" }}>{item.label}</span>
                          <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "#6c5ce7" }}>{item.val}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section> */}

            {/* ── ONBOARD ── */}
            {/* <section className="q-onboard-section">
              <div className="container">
                <div className="q-onboard-grid">
                  <div className="q-onboard-visual">
                    <div className="q-world-stat">186</div>
                    <div style={{ color: "#a29bfe", fontWeight: 700, fontSize: "1.1rem", marginBottom: 12 }}>Countries</div>
                    <p style={{ color: "#aaa", fontSize: "0.875rem", lineHeight: 1.7 }}>
                      Onboard your new talent pool to Quiety, invite them to projects, sign contracts and kick off the work.
                    </p>
                    <ul className="q-onboard-list">
                      <li>Fast Freelancer Onboarding</li>
                      <li>Onboarding Contracts for 186 countries</li>
                      <li>Localised skills and legal agreements</li>
                      <li>Community skills and legal agreements</li>
                    </ul>
                    <Link to="/jobs" className="q-btn-primary">
                      Explore More <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                  <div>
                    <div className="q-kicker">Global Reach</div>
                    <h2 className="q-section-title">
                      Onboard Your <span style={{ color: "#6c5ce7" }}>Freelancers</span> From 186 Countries
                    </h2>
                    <p className="q-section-sub mb-4">
                      Onboard your new talent pool to Quiety, invite them to projects, sign contracts and kick off the work.
                    </p>
                    {[
                      { icon: "ri-global-line", title: "Global talent pool", desc: "Access top talent from 186 countries with verified profiles." },
                      { icon: "ri-file-text-line", title: "Automated contracts", desc: "Legally compliant contracts generated in seconds." },
                      { icon: "ri-shield-check-line", title: "Secure & compliant", desc: "GDPR compliant with enterprise-grade security." },
                    ].map((f) => (
                      <div key={f.title} className="q-payment-feature mb-3" style={{ background: "#f8f7ff" }}>
                        <div className="q-feature-icon"><i className={f.icon}></i></div>
                        <div>
                          <div className="q-feature-title">{f.title}</div>
                          <div className="q-feature-desc">{f.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section> */}

            {/* ── TESTIMONIALS ── */}
            <section className="q-reviews-section">
              <div className="container">
                <div className="q-section-head mb-5">
                  <div>
                    <div className="q-kicker" style={{ color: "#a29bfe" }}>{testimonialKicker}</div>
                    <h2 className="q-section-title q-section-title-light">
                      {testimonialHeading}<br />
                      <span style={{ color: "#a29bfe" }}>{testimonialHighlight}</span>
                    </h2>
                  </div>
                
                </div>
                <div className="q-reviews-grid">
                  {testimonialItems.map((t, i) => (
                    <div key={i} className="q-review-card">
                      <div className="q-stars">
                        {Array.from({ length: Math.max(1, Math.min(5, Number(t.rating) || 5)) }).map((_, starIndex) => (
                          <i className="ri-star-fill" key={starIndex}></i>
                        ))}
                      </div>
                      <p className="q-review-text">"{t.text}"</p>
                      <div className="q-reviewer">
                        <div className="q-reviewer-avatar">{t.name[0]}</div>
                        <div>
                          <div className="q-reviewer-name">{t.name}</div>
                          <div className="q-reviewer-role">{t.role} · {t.company}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* ── HIRE BY PROFESSION ── */}
            {/* <section className="q-profession-section">
              <div className="container">
                <div className="q-section-head">
                  <div>
                    <div className="q-kicker">Top Freelancers</div>
                    <h2 className="q-section-title">Hire by Profession</h2>
                    <p className="q-section-sub">Connect your talent pool to Quiety and start working.</p>
                  </div>
                  <Link to="/jobs" className="q-btn-outline">
                    View All <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
                <div className="q-profession-tabs">
                  {["Full Stack Developer", "Accountant", "Promotional Operation"].map((tab, i) => (
                    <button key={tab} className={`q-prof-tab${i === 0 ? " active" : ""}`}>{tab}</button>
                  ))}
                </div>
                <div className="q-freelancer-grid">
                  {[
                    { name: "Rafhia Talunder", role: "UI/UX Designer", skills: ["Figma", "React", "CSS"] },
                    { name: "Aminul Islam", role: "Full Stack Developer", skills: ["Node.js", "React", "MongoDB"] },
                    { name: "Shawrab", role: "Product Manager", skills: ["Agile", "Jira", "Strategy"] },
                  ].map((fl) => (
                    <div key={fl.name} className="q-freelancer-card">
                      <div className="q-fl-avatar">{fl.name[0]}</div>
                      <div className="q-fl-name">{fl.name}</div>
                      <div className="q-fl-role">{fl.role}</div>
                      <div className="q-fl-skills">
                        {fl.skills.map((s) => <span key={s} className="q-fl-skill">{s}</span>)}
                      </div>
                      <Link to="/jobs" className="q-fl-btn">View Profile</Link>
                    </div>
                  ))}
                </div>
              </div>
            </section> */}


 <section className="q-app-section">
    <div className="container">
      <div className="q-app-grid">
        <div>
           <h2 className="q-app-title">{profileTitle}<br /><span>{profileHighlight}</span></h2>
          <p className="q-app-desc">{profileDescription}</p>
          <div className="q-app-features">
        {profileFeatures.map((f) => (
  <div key={f} className="q-app-feat-row">
    <div className="q-feat-check">
      <i className="ri-check-line"></i>
    </div>
    {f}
  </div>
))}

          </div>
          <form onSubmit={sendAppLink} className="q-sms-box">
            <input
              className="q-sms-input"
              placeholder={profileEmailPlaceholder}
              type="email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="q-sms-btn" type="submit">{sendingAppLink ? "Sending..." : profileButtonText}</button>
          </form>
          <div className="q-store-btns">
            <a href={googlePlayUrl} className="q-store-btn" target={googlePlayUrl.startsWith("http") ? "_blank" : undefined} rel={googlePlayUrl.startsWith("http") ? "noopener noreferrer" : undefined}><i className="ri-google-play-fill"></i><div className="q-store-btn-text"><div className="q-store-btn-sub">{googlePlayLabel}</div><div className="q-store-btn-name">{googlePlayTitle}</div></div></a>
            <a href={appStoreUrl} className="q-store-btn" target={appStoreUrl.startsWith("http") ? "_blank" : undefined} rel={appStoreUrl.startsWith("http") ? "noopener noreferrer" : undefined}><i className="ri-apple-fill"></i><div className="q-store-btn-text"><div className="q-store-btn-sub">{appStoreLabel}</div><div className="q-store-btn-name">{appStoreTitle}</div></div></a>
          </div>
        </div>
        <div className="q-app-visual">
         <img src={profileImage} alt="App Mockup" className="w-100" />
        </div>
      </div>
    </div>
  </section>


            {/* ── FAQ ── */}
            <FAQSection
              items={faqItems}
              title={firstValue(homeLayout?.faq_heading, "Frequently Asked Question")}
              intro={firstValue(
                homeLayout?.faq_description,
                "Answers to common questions about jobs, recruiters, applications, and using gojobify."
              )}
              kicker={faqKicker}
              buttonText={faqButtonText}
              buttonLink={faqButtonLink}
            />

            {/* ── BLOG ── */}
            {latestBlogs.length > 0 && (
              <section className="q-section-subtle">
                <div className="container">
                  <div className="q-section-head">
                    <div>
                      <div className="q-kicker">{blogKicker}</div>
                      <h2 className="q-section-title">{blogHeading}</h2>
                    </div>
                    <Link to={blogButtonLink} className="q-btn-outline">
                      {blogButtonText} <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                  <div className="row g-4">
                    {latestBlogs.slice(0, 3).map((blog) => (
                      <div className="col-md-4" key={blog.slug}>
                        <div className="q-blog-card">
                          <div className="q-blog-img-placeholder">
                            <i className="ri-article-line"></i>
                          </div>
                          <div className="q-blog-body">
                            <div className="q-blog-cat">{blogCategoryLabel}</div>
                            <h3 className="q-blog-title">{blog.title}</h3>
                            <p className="q-blog-desc">{blogDescription}</p>
                            <Link to={`/blogs/${blog.slug}`} className="q-blog-read">
                              {blogReadText} <i className="ri-arrow-right-line"></i>
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )} 
          
          </>
        )}
        
      </div>
   </PublicLayout>
     </>
  );
};

/* ── FAQ Sub-component (stateful) ── */
const FAQ_ITEMS = [
  { q: "What is my skill?", a: "We match you with jobs based on your skills, experience, and career goals using AI-powered matching technology." },
  { q: "How does this work?", a: "Sign up, complete your profile, and start browsing thousands of curated job listings and freelance projects." },
  { q: "Where can I view new listings?", a: "New listings are updated every few minutes. Visit the Jobs page or subscribe to alerts for instant notifications." },
  { q: "Where can I view new positions and find?", a: "Use our advanced search filters to find positions by location, category, salary range, and employment type." },
  { q: "Where can I view new listings and find?", a: "You can save searches and receive email alerts for your preferred job categories and locations." },
  { q: "Where can I view new positions?", a: "Our recruiter dashboard gives hiring managers access to a complete pipeline of qualified candidates." },
];

const FAQSection = ({
  items = FAQ_ITEMS,
  title = "Frequently Asked Question",
  intro = "",
  kicker = "FAQ",
  buttonText = "Ask a Question",
  buttonLink = "/contact-us",
}) => {
  const [open, setOpen] = useState(null);
  return (
    <section className="q-faq-section">
      <div className="container">
        <div className="q-faq-grid">
          <div>
            <div className="q-kicker">{kicker}</div>
            <h2 className="q-section-title">{title}</h2>
            <p className="q-section-sub mt-2 mb-4">
              {intro}
            </p>
            <Link to={buttonLink} className="q-btn-primary">
              {buttonText} <i className="ri-question-line"></i>
            </Link>
          </div>
          <div>
            {items.map((item, i) => (
              <div key={i} className={`q-faq-item${open === i ? " open" : ""}`} onClick={() => setOpen(open === i ? null : i)}>
                <div className="q-faq-question">
                  {item.q}
                  <div className="q-faq-icon"><i className="ri-add-line"></i></div>
                </div>
                <div className="q-faq-answer">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PublicHome;
